import { Stack, Text } from '@mantine/core';
import { PiCheckCircle } from 'react-icons/pi';
import { type LineRef, lineKey } from '@/models/lineKey';
import type { TSplitLine } from '@/models/split-plan';

/** A committed coverage row, or a plan draft's line that has no coverage row yet. */
type ContentLine = LineRef & {
  coverageId?: number;
  deliveredAt?: string | null;
};

/**
 * What an installment actually covers — BizDev's description of it and the services
 * assigned to it. The SR otherwise sees only a sequence number and an amount, which
 * says nothing about what "awaiting delivery" is waiting on.
 */
export function InstallmentContents({
  coveredLines,
  note,
  selectedLines,
  emptyLabel = 'No services — pre-payment',
}: {
  coveredLines: ContentLine[];
  note: string | null;
  selectedLines: TSplitLine[];
  emptyLabel?: string;
}) {
  const nameOf = (ref: ContentLine): string => {
    const key = lineKey(ref);
    const hit = selectedLines.find((l) => lineKey(l) === key);
    // A line the plan no longer lists still belongs to the installment; naming it by
    // service id beats rendering a blank row.
    return hit?.serviceName ?? `Service #${ref.serviceId}`;
  };

  return (
    <Stack gap={2}>
      {note && (
        <Text fz="xs" c="dimmed">
          {note}
        </Text>
      )}
      {coveredLines.length === 0 ? (
        <Text fz="xs" c="dimmed" fs="italic">
          {emptyLabel}
        </Text>
      ) : (
        coveredLines.map((l) => (
          <Text
            key={l.coverageId ?? lineKey(l)}
            fz="xs"
            c={l.deliveredAt ? 'teal' : undefined}
          >
            {l.deliveredAt ? (
              <PiCheckCircle
                size={11}
                style={{ verticalAlign: -1, marginRight: 4 }}
              />
            ) : (
              <span style={{ marginRight: 6 }}>·</span>
            )}
            {nameOf(l)}
          </Text>
        ))
      )}
    </Stack>
  );
}
